// VideoRecorder.tsx
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Video, Mic, Circle, Square, AlertCircle, CheckCircle } from 'lucide-react';

interface VideoRecorderProps {
  onRecordingComplete: (blob: Blob) => void;
  onRecordingStart?: () => void;
  timeLimitSeconds: number;
  autoStart?: boolean;
  questionNumber: number;
}

const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onRecordingComplete,
  onRecordingStart,
  timeLimitSeconds,
  autoStart = true,
  questionNumber
}) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(timeLimitSeconds);
  const [isRecordingStarted, setIsRecordingStarted] = useState<boolean>(false);
  const [recordingCompleted, setRecordingCompleted] = useState<boolean>(false);
  const [ownStream, setOwnStream] = useState<MediaStream | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamAssignedRef = useRef<boolean>(false);

  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported("video/webm; codecs=av01")) {
      return "video/webm; codecs=av01";
    }
    if (MediaRecorder.isTypeSupported("video/webm; codecs=vp9")) {
      return "video/webm; codecs=vp9";
    }
    if (MediaRecorder.isTypeSupported("video/webm; codecs=vp8")) {
      return "video/webm; codecs=vp8";
    }
    if (MediaRecorder.isTypeSupported("video/mp4")) {
      return "video/mp4"; // Safari fallback (H.264)
    }
    return ""; // Let browser decide
  };

  const mimeType = getSupportedMimeType();

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    error: recorderError
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    mediaRecorderOptions: mimeType ? { mimeType } : undefined,
    blobPropertyBag: { type: mimeType || "video/webm" },
    onStop: async (_, blob) => {
      setRecordingCompleted(true);
      if (timerRef.current) clearInterval(timerRef.current);
      onRecordingComplete(blob);
    }
  });

  // Get user media
  useEffect(() => {
    let mounted = true;

    const initStream = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (mounted) {
          setOwnStream(stream);
          setPermissionError(null);
        } else {
          stream.getTracks().forEach(track => track.stop());
        }
      } catch (err: any) {
        if (!mounted) return;

        if (err.name === "NotAllowedError") {
          setPermissionError("Camera & microphone permissions denied.");
        } else if (err.name === "NotFoundError") {
          setPermissionError("No camera or microphone found.");
        } else {
          setPermissionError(`Error accessing media devices: ${err.message}`);
        }
      }
    };

    initStream();
    return () => { mounted = false };
  }, []);

  // Apply preview stream
  useEffect(() => {
    if (videoRef.current && ownStream && !streamAssignedRef.current) {
      videoRef.current.srcObject = ownStream;
      streamAssignedRef.current = true;
    }
  }, [ownStream]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (ownStream) {
        ownStream.getTracks().forEach(t => t.stop());
      }
    };
  }, [ownStream]);

  const isPermissionDenied = permissionError !== null || recorderError === "permission_denied";
  const isAcquiring = !ownStream && !permissionError;

  // Auto-start
  useEffect(() => {
    if (autoStart && ownStream && !isRecordingStarted && status === "idle") {
      const t = setTimeout(() => handleStartRecording(), 800);
      return () => clearTimeout(t);
    }
  }, [autoStart, ownStream, isRecordingStarted, status]);

  // Timer
  useEffect(() => {
    if (status === "recording") {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  const handleStartRecording = useCallback(() => {
    setIsRecordingStarted(true);
    setTimeRemaining(timeLimitSeconds);
    startRecording();
    onRecordingStart?.();
  }, [startRecording, timeLimitSeconds, onRecordingStart]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
    if (timerRef.current) clearInterval(timerRef.current);
  }, [stopRecording]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const isTimeWarning = timeRemaining <= 30;

  // Permission error UI
  if (isPermissionDenied) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 min-h-[400px]">
        <AlertCircle className="w-8 h-8 text-red-600 mb-3" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Permission Required</h3>
        <p className="text-gray-600 text-center mb-6">{permissionError}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-brand-500 text-white rounded-lg"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Completed View
  if (recordingCompleted && mediaBlobUrl) {
    return (
      <div className="flex flex-col items-center p-6 bg-gray-50">
        <CheckCircle className="w-6 h-6 text-green-600 mb-3" />
        <h3 className="text-green-700 font-medium">Recording Complete</h3>
        <video src={mediaBlobUrl} controls className="mt-4 w-full rounded" />
        <p className="text-gray-500 text-sm mt-2">
          Response for Question {questionNumber} recorded. Uploading...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50">
      {/* PREVIEW AREA */}
      <div className="relative w-full aspect-video bg-black">
        <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />

        {/* Recording indicator */}
        {status === "recording" && (
          <div className="absolute top-4 left-4 bg-red-600 px-3 py-1.5 rounded-full animate-pulse flex items-center">
            <Circle className="w-3 h-3 text-white mr-2" />
            <span className="text-white text-sm font-medium">REC</span>
          </div>
        )}

        {/* Timer */}
        <div className={`absolute top-4 right-4 px-4 py-2 rounded-lg ${isTimeWarning ? "bg-red-100" : "bg-white"}`}>
          <span className={`font-mono text-2xl font-bold ${isTimeWarning ? "text-red-600" : "text-brand-500"}`}>
            {formatTime(timeRemaining)}
          </span>
        </div>

        {/* Preparing camera */}
        {isAcquiring && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white">
            <div>
              <div className="animate-spin w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p>Preparing camera...</p>
            </div>
          </div>
        )}
      </div>

      {/* CONTROLS */}
      <div className="p-4 bg-white border-t">
        <div className="flex justify-center">
          {status === "recording" ? (
            <button
              onClick={handleStopRecording}
              className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg"
            >
              <Square className="w-5 h-5 mr-2" />
              Stop Recording
            </button>
          ) : (
            !recordingCompleted &&
            !isRecordingStarted && (
              <button
                onClick={handleStartRecording}
                disabled={!ownStream}
                className="flex items-center px-6 py-3 bg-brand-500 text-white rounded-lg disabled:opacity-50"
              >
                <Circle className="w-5 h-5 mr-2" />
                Start Recording
              </button>
            )
          )}
        </div>

        <div className="flex justify-center gap-4 mt-4 text-sm text-gray-500">
          <span className="flex items-center"><Video className="w-4 h-4 mr-1 text-green-500" /> Camera Active</span>
          <span className="flex items-center"><Mic className="w-4 h-4 mr-1 text-green-500" /> Microphone Active</span>
        </div>

        {recorderError && (
          <div className="mt-4 p-3 bg-red-50 text-center text-red-600 border border-red-200 rounded">
            {recorderError}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRecorder;
